import styled, { css, keyframes } from 'styled-components';

import { SPACING } from '~/common/constants/spacing';
import { GRID } from '~/common/constants/grid';
import { Z_INDEX } from '~/common/constants/z-index';

interface IOverlay {
  readonly isClosing: boolean;
}

export const Overlay = styled.div<IOverlay>`
  background: rgba(0, 0, 0, 0.4);
  position: fixed;
  width: 100%;
  height: 100%;
  left: 0;
  top: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: ${Z_INDEX.MODAL};
  animation: ${({ isClosing }) =>
    !isClosing ? 'fadeinModal 1s' : 'fadeoutModal 1s'};

  @keyframes fadeinModal {
    0% {
      opacity: 0;
    }
    100% {
      opacity: 1;
    }
  }

  @keyframes fadeoutModal {
    0% {
      opacity: 1;
    }
    100% {
      opacity: 0;
    }
  }
`;

interface IModalContainer {
  readonly width: string;
  readonly height: string;
  readonly maxHeight?: string;
  readonly isClosing: boolean;
}

const fadeIn = keyframes`
  0% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
`;

const slideIn = keyframes`
  0% {
    transform: translateY(400px);
    animation-timing-function: ease-out;
  }
  60% {
    transform: translateY(-30px);
    animation-timing-function: ease-in;
  }
  80% {
    transform: translateY(10px);
    animation-timing-function: ease-out;
  }
  100% {
    transform: translateY(0px);
    animation-timing-function: ease-in;
  }
`;

const slideOut = keyframes`
  0% {
    transform: translateY(0px);
    animation-timing-function: ease-in;
  }
  20% {
    transform: translateY(-10px);
    animation-timing-function: ease-out;
  }
  40% {
    transform: translateY(30px);
    animation-timing-function: ease-in;
  }
  100% {
    transform: translateY(400px);
    animation-timing-function: ease-out;
  }
`;

export const ModalContainer = styled.div<IModalContainer>`
  position: relative;
  display: flex;
  flex-direction: column;
  width: ${({ width }) => width || 'fit-content'};
  height: ${({ height }) => height || 'auto'};
  min-height: 0;
  max-height: ${({ maxHeight }) => maxHeight || '90%'};
  overflow: hidden;
  background-color: ${({ theme }) => theme.neutral[950]};
  z-index: ${Z_INDEX.MODAL + 1};

  ${({ isClosing }) =>
    isClosing &&
    css`
      animation: ${slideOut} 0.6s forwards;
    `}

  ${({ isClosing }) =>
    !isClosing &&
    css`
      animation: ${fadeIn} 0.6s, ${slideIn} 0.6s;
    `}


    @media ${GRID._XS} {
    position: absolute;
    width: 100%;
    bottom: 0;
  }
`;

interface IHeader {
  readonly $isSmallGapPadding?: boolean;
}

export const Header = styled.header<IHeader>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  height: 70px;
  padding: ${({ $isSmallGapPadding }) =>
    $isSmallGapPadding ? SPACING._16 : SPACING._32};
  background-color: ${({ theme }) => theme.primary_header};

  @media ${GRID._XS} {
    height: fit-content;
  }
`;

export const StyHeaderAling = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`;

interface IStyModalContent {
  readonly justifyContent: string;
  readonly $isSmallGapPadding?: boolean;
}

export const StyModalContent = styled.div<IStyModalContent>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: ${({ justifyContent }) => justifyContent || 'center'};
  width: 100%;
  flex: 1 1 auto;
  min-height: 0;
  padding: ${({ $isSmallGapPadding }) =>
    $isSmallGapPadding ? SPACING._16 : SPACING._32};
  overflow-y: auto;
  overscroll-behavior: contain;
  gap: ${SPACING._16};

  @media ${GRID._LG} {
    padding: ${({ $isSmallGapPadding }) =>
      $isSmallGapPadding ? SPACING._16 : SPACING._24};
  }

  @media ${GRID._SM} {
    padding: ${SPACING._16};
  }

  @media ${GRID._XS} {
    padding: ${SPACING._16};
  }
`;

export const StyCloseContent = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  gap: ${SPACING._32};

  @media ${GRID._LG} {
    gap: ${SPACING._24};
  }

  @media ${GRID._SM} {
    gap: ${SPACING._16};
  }

  @media ${GRID._XS} {
    gap: ${SPACING._16};
  }
`;
